import { CohortBaseDetail, GetCurrentUser200 } from '@platform/client/schemas';
import { ClientProgramNames } from '../constants';

/**Helper that takes db value of corporate_client.url_param and returns the corresponding program string. */
export const getProgramNameFromParam = (clientName: string | undefined | null) => {
  let programName: string;
  switch (clientName?.toLowerCase()) {
    case 'sap':
      programName = ClientProgramNames.SAP;
      break;
    case 'f5':
      programName = ClientProgramNames.F5;
      break;
    case 'lumen':
      programName = ClientProgramNames.LUMEN;
      break;
    case 'ey':
      programName = ClientProgramNames.EY;
      break;
    case 'reckitt':
      programName = ClientProgramNames.RECKITT;
      break;
    case 'philips':
      programName = ClientProgramNames.PHILIPS;
      break;
    default:
      programName = ClientProgramNames.INDIVIDUAL;
      break;
  }
  return programName;
};

/** Helper function to determine if user role is inactive for conditional platform access */
export const isInactiveUser = (user: GetCurrentUser200 | undefined): boolean => {
  if (!user) return false;
  return user.accountType === 'experteer' && user.role === 'inactive';
};

export const isCorporateProNoCohort = (user: GetCurrentUser200 | undefined): boolean => {
  if (!user) return false;
  return user.accountType === 'experteer' && !!user.corporatePartner && !user.cohort;
};

export const isNonCorporatePro = (user: GetCurrentUser200 | undefined): boolean => {
  if (!user) return false;
  return user.accountType === 'experteer' && !user.corporatePartner;
};

type CohortDateStrings = {
  kickoff: string;
  engageStart: string;
  engageEnd: string;
  reflect: string;
  matchingStart: string;
  matchingEnd: string;
  matchingRange: string;
  planningStart: string;
  planningEnd: string;
  planningRange: string;
  actionStart: string;
  actionEnd: string;
  actionRange: string;
  wrapUp: string;
};

/** Returns the commonly used cohort dates formatted to strings representing single dates or ranges. */
export const cohortDatesToStrings = (cohort: CohortBaseDetail): CohortDateStrings => {
  const matchingStart = new Date(cohort.progressDates.matchingStart).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const matchingEnd = new Date(cohort.progressDates.matchingEnd).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const planningStart = new Date(cohort.progressDates.planningStart).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const planningEnd = new Date(cohort.progressDates.planningEnd).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const actionStart = new Date(cohort.progressDates.actionStart).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const actionEnd = new Date(cohort.progressDates.actionEnd).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  return {
    kickoff: new Date(cohort.kickoffDate).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
    engageStart: new Date(cohort.engagementStartDate).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
    engageEnd: new Date(cohort.engagementEndDate).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
    reflect: new Date(cohort.progressDates.reflect).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
    matchingStart: matchingStart,
    matchingEnd: matchingEnd,
    matchingRange: `${matchingStart}-${matchingEnd}`,
    planningStart: planningStart,
    planningEnd: planningEnd,
    planningRange: `${planningStart}-${planningEnd}`,
    actionStart: actionStart,
    actionEnd: actionEnd,
    actionRange: `${actionStart}-${actionEnd}`,
    wrapUp: new Date(cohort.wrapupDate).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
  };
};
